import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CreateTenantModal } from './components/CreateTenantModal';
import { InitialShiftManagerModal } from './components/InitialShiftManagerModal';
import { TenantsTable } from './components/TenantsTable';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useTenantsPageController } from './useTenantsPageController';
import { useE2eRunnerController } from './useE2eRunnerController';
import { E2eRunConfirmModal } from './components/E2eRunConfirmModal';
import classes from './tenants-page.module.scss';

// Architectural summary:
// Super-admin tenants management screen (Hebrew, RTL).
// Touches:
//   - tenants.service.ts (create / updateActive / deactivationImpact)
//   - AdminEventsProvider (live `tenants` snapshot — page does NOT open its own SSE)
//   - AuthenticatedRoutes /tenants (gated by SuperAdminOnly)
//   - tenants-page.module.scss (theme tokens only)
//   - Mantine MantineProvider in GlobalProvider must wrap this tree
// Breaking change risk: changing column ids/labels affects screenshots
// and the associated component styles; renaming class keys breaks the generated declarations.
export const TenantsPage: React.FC = () => {
  const {
    tenants,
    isLoading,
    companyName,
    isCreating,
    updatingTenantIds,
    isCreateOpen,
    expandedTenantId,
    displayedError,
    tenantsCountLabel,
    setCompanyName,
    setIsCreateOpen,
    handleCreateTenant,
    toggleTenantActive,
    openCreateModal,
    onToggleExpanded,
    getTenantOwnerEmailDraft,
    setTenantOwnerEmailDraft,
    getTenantIsTestDraft,
    setTenantIsTestDraft,
    getTenantRestigoBranchDraft,
    setTenantRestigoBranchDraft,
    getTenantRestigoKeyDraft,
    setTenantRestigoKeyDraft,
    getTenantOntopoKeyDraft,
    setTenantOntopoKeyDraft,
    getTenantWoltVenueDraft,
    setTenantWoltVenueDraft,
    disconnectWolt,
    syncWoltMenu,
    woltConfirm,
    confirmWoltAction,
    cancelWoltConfirm,
    getTenantTenbisDraft,
    setTenantTenbisDraft,
    testTenbisLogin,
    disconnectTenbis,
    syncTenbisMenu,
    getTenantCibusDraft,
    setTenantCibusDraft,
    testCibusLogin,
    testCibusPosWs,
    exportCibusMenu,
    disconnectCibus,
    getStagedPhotoFile,
    getTenantPhotoPreviewUrl,
    showRemovePhotoControl,
    hasTenantPanelChanges,
    attendanceLocationSettings,
    stageTenantPhoto,
    stageRemoveTenantPhoto,
    saveTenantPanel,
    shiftManagerTenant,
    shiftManagerUsername,
    shiftManagerPassword,
    shiftManagerConfirmPassword,
    shiftManagerPermissions,
    isSavingShift,
    setShiftManagerUsername,
    setShiftManagerPassword,
    setShiftManagerConfirmPassword,
    setShiftManagerPermissions,
    closeShiftManagerModal,
    submitInitialShiftManager,
    toggleTenantShift,
  } = useTenantsPageController();

  // Scalar dep (Rule 28): polling runs only while an is_test tenant's settings panel is open.
  const isTestPanelOpen = tenants.some((tenant) => tenant.id === expandedTenantId && tenant.isTest);
  const e2eRunner = useE2eRunnerController(isTestPanelOpen);

  return (
    <div className={classes.container}>
      <div className={classes.card}>
        <div className={classes.toolbar}>
          <div className={classes.titleBlock}>
            <h2 className={classes.title}>ניהול חברות</h2>
            <p className={classes.subtitle}>{tenantsCountLabel}</p>
          </div>
          <button
            type="button"
            className={classes.createButton}
            onClick={openCreateModal}
            data-testid="tenants-create"
          >
            <span className={classes.createLabel}>צור חברה</span>
            <FontAwesomeIcon icon={faPlus as IconProp} className={classes.plusIcon} aria-hidden="true" />
          </button>
        </div>

        {displayedError && <p className={classes.error}>{displayedError}</p>}
        <TenantsTable
          tenants={tenants}
          isLoading={isLoading}
          updatingTenantIds={updatingTenantIds}
          expandedTenantId={expandedTenantId}
          onToggleExpanded={onToggleExpanded}
          onToggleTenantActive={(tenant) => {
            void toggleTenantActive(tenant);
          }}
          onToggleTenantShift={(tenant, enabled) => {
            void toggleTenantShift(tenant, enabled);
          }}
          getTenantOwnerEmailDraft={getTenantOwnerEmailDraft}
          setTenantOwnerEmailDraft={setTenantOwnerEmailDraft}
          getTenantIsTestDraft={getTenantIsTestDraft}
          setTenantIsTestDraft={setTenantIsTestDraft}
          getTenantRestigoBranchDraft={getTenantRestigoBranchDraft}
          setTenantRestigoBranchDraft={setTenantRestigoBranchDraft}
          getTenantRestigoKeyDraft={getTenantRestigoKeyDraft}
          setTenantRestigoKeyDraft={setTenantRestigoKeyDraft}
          getTenantOntopoKeyDraft={getTenantOntopoKeyDraft}
          setTenantOntopoKeyDraft={setTenantOntopoKeyDraft}
          getTenantWoltVenueDraft={getTenantWoltVenueDraft}
          setTenantWoltVenueDraft={setTenantWoltVenueDraft}
          onDisconnectWolt={(tenant) => {
            void disconnectWolt(tenant);
          }}
          onSyncWoltMenu={(tenant) => {
            void syncWoltMenu(tenant);
          }}
          getTenantTenbisDraft={getTenantTenbisDraft}
          setTenantTenbisDraft={setTenantTenbisDraft}
          onTestTenbisLogin={(tenant) => {
            void testTenbisLogin(tenant);
          }}
          onDisconnectTenbis={disconnectTenbis}
          onSyncTenbisMenu={(tenant) => {
            void syncTenbisMenu(tenant);
          }}
          getTenantCibusDraft={getTenantCibusDraft}
          setTenantCibusDraft={setTenantCibusDraft}
          onTestCibusLogin={(tenant) => {
            void testCibusLogin(tenant);
          }}
          onTestCibusPosWs={(tenant) => {
            void testCibusPosWs(tenant);
          }}
          onExportCibusMenu={(tenant) => {
            void exportCibusMenu(tenant);
          }}
          onDisconnectCibus={disconnectCibus}
          getStagedPhotoFile={getStagedPhotoFile}
          getTenantPhotoPreviewUrl={getTenantPhotoPreviewUrl}
          showRemovePhotoControl={showRemovePhotoControl}
          hasTenantPanelChanges={hasTenantPanelChanges}
          attendanceLocationSettings={attendanceLocationSettings}
          onStageTenantPhoto={stageTenantPhoto}
          onStageRemoveTenantPhoto={stageRemoveTenantPhoto}
          onSaveTenantPanel={(tenant) => {
            void saveTenantPanel(tenant);
          }}
          e2eRunner={e2eRunner}
        />
      </div>

      <ConfirmModal
        opened={woltConfirm !== null}
        title={woltConfirm?.title ?? ''}
        message={woltConfirm?.message ?? ''}
        confirmLabel={woltConfirm?.confirmLabel ?? ''}
        onConfirm={() => {
          void confirmWoltAction();
        }}
        onCancel={cancelWoltConfirm}
        dataTestId="tenants-wolt-confirm"
      />

      <CreateTenantModal
        isOpen={isCreateOpen}
        companyName={companyName}
        isCreating={isCreating}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={handleCreateTenant}
        onCompanyNameChange={setCompanyName}
      />

      <E2eRunConfirmModal
        tier={e2eRunner.pendingTier}
        branch={e2eRunner.branchDraft}
        isBranchValid={e2eRunner.isBranchDraftValid}
        defaultBranch={e2eRunner.status?.branch ?? null}
        isBusy={e2eRunner.isBusy}
        onBranchChange={e2eRunner.setBranchDraft}
        onCancel={e2eRunner.cancelLaunch}
        onConfirm={e2eRunner.confirmLaunch}
      />
      <InitialShiftManagerModal
        tenant={shiftManagerTenant}
        username={shiftManagerUsername}
        password={shiftManagerPassword}
        confirmPassword={shiftManagerConfirmPassword}
        permissions={shiftManagerPermissions}
        isSaving={isSavingShift}
        onUsernameChange={setShiftManagerUsername}
        onPasswordChange={setShiftManagerPassword}
        onConfirmPasswordChange={setShiftManagerConfirmPassword}
        onPermissionChange={(permission, checked) => {
          setShiftManagerPermissions((current) => ({ ...current, [permission]: checked }));
        }}
        onClose={closeShiftManagerModal}
        onSubmit={submitInitialShiftManager}
      />
    </div>
  );
};
